import { Alert, Button, Descriptions, message, Modal, Radio, Tag } from 'antd'
import { DaoProjectItemType, DaoProposalType } from 'Modules'
import { useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { DAO_MESSAGES, DaoSaleOutcomeEnum } from 'constants/dao-voting'
import { useAssetStateMachineContract } from 'hooks/dao-voting/use-asset-state-machine-contract'
import { daoTxErrorMessage } from 'hooks/dao-voting/use-prepared-tx'
import { daoApiErrorMessage } from 'modules/dao-voting/utils'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

interface ReportSaleResultModalProps {
  open: boolean
  project: DaoProjectItemType
  /** the Passed proposal whose sale outcome is being recorded */
  proposal?: DaoProposalType | null
  onClose: () => void
  onSuccess: () => void
}

/**
 * Report Sale Result (SRS 5.8) - irreversible, single-admin. Two steps like
 * Create Pool: step 1 picks the outcome, step 2 reviews and records.
 */
const ReportSaleResultModal = ({
  open,
  project,
  proposal,
  onClose,
  onSuccess,
}: ReportSaleResultModalProps) => {
  const [outcome, setOutcome] = useState<DaoSaleOutcomeEnum>(DaoSaleOutcomeEnum.SUCCESS)
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)

  const { address } = useAccount()
  const { reportSaleResultOnChain } = useAssetStateMachineContract()

  useEffect(() => {
    if (open) {
      setOutcome(DaoSaleOutcomeEnum.SUCCESS)
      setStep(1)
    }
  }, [open])

  const isSuccess = outcome === DaoSaleOutcomeEnum.SUCCESS
  const targetLabel = isSuccess ? 'Sale Success' : 'Sale Failed'

  const handleConfirm = async () => {
    if (!proposal?._id) {
      message.error('No passed proposal found for this project')
      return
    }
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    try {
      setSubmitting(true)

      // 1. BE records the outcome and prepares the reportSaleResult calldata
      const res = await AppAdmin.reportDaoVotingSaleResult(proposal._id, {
        success: isSuccess,
      })

      // 2. Manager wallet sends AssetStateMachine.reportSaleResult (SRS 5.8)
      const reportCall = res.data?.data?.onChainReportSaleResultCall
      if (reportCall) {
        try {
          onOpenModalProcessing()
          await reportSaleResultOnChain(address, reportCall)
        } catch (chainError) {
          // BE record already exists - surface the on-chain failure explicitly
          logError('Error reporting sale result on-chain: ', chainError)
          message.error(
            daoTxErrorMessage(chainError) || 'Sale result recorded but on-chain report failed'
          )
          onSuccess()
          onClose()
          return
        } finally {
          onCloseModalProcessing()
        }
      }

      message.success(DAO_MESSAGES.S05)
      onSuccess()
      onClose()
    } catch (error: any) {
      logError('Error reporting sale result: ', error)
      message.error(daoApiErrorMessage(error) || 'Failed to record sale result')
    } finally {
      setSubmitting(false)
    }
  }

  const stepOne = (
    <>
      <p>Record the real-world outcome for {project.fullName}.</p>
      <Radio.Group
        value={outcome}
        onChange={(e) => setOutcome(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio value={DaoSaleOutcomeEnum.SUCCESS}>Success</Radio>
        <Radio value={DaoSaleOutcomeEnum.FAILED}>Failed</Radio>
      </Radio.Group>
      <Alert
        type="warning"
        showIcon
        message={`This will move ${project.fullName} to ${targetLabel} and cannot be undone.`}
      />
    </>
  )

  const stepTwo = (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`This action is irreversible and will move ${project.fullName} to ${targetLabel}.`}
      />
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Linked PTO Project">{project.fullName}</Descriptions.Item>
        <Descriptions.Item label="Proposal">{proposal?.title || '--'}</Descriptions.Item>
        <Descriptions.Item label="Sale Result">
          {isSuccess ? <Tag color="green">Success</Tag> : <Tag color="error">Failed</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Asset Status After">{targetLabel}</Descriptions.Item>
        <Descriptions.Item label="Next Step">
          {isSuccess
            ? 'Create the RWA Rewards Pool to distribute sale proceeds'
            : 'Create the Refund Pool so holders can claim refunds'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  )

  const footer =
    step === 1
      ? [
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button key="continue" type="primary" onClick={() => setStep(2)}>
            Continue
          </Button>,
        ]
      : [
          <Button key="back" onClick={() => setStep(1)}>
            Back
          </Button>,
          <Button key="confirm" type="primary" loading={submitting} onClick={handleConfirm}>
            Confirm & Record
          </Button>,
        ]

  return (
    <Modal
      title="Report Sale Result"
      open={open}
      onCancel={onClose}
      maskClosable={false}
      footer={footer}
    >
      {step === 1 ? stepOne : stepTwo}
    </Modal>
  )
}

export default ReportSaleResultModal
